import { Group, Text, type TextProps } from "@mantine/core";
import { IconArrowDownRight, IconArrowUpRight, IconMinus } from "@tabler/icons-react";
import { semanticRoles, trendRole, type Trend } from "@how/design-system";

export interface DeltaValueProps extends Omit<TextProps, "children" | "c"> {
  /** Already-formatted change, for example "+12.4%" or "-3 sessions". Formatting belongs to the caller. */
  readonly children: string;
  /**
   * Direction of the change. Callers pass it explicitly because "up" is not always favorable: a rise in failed jobs
   * is a `down` trend for the reader even though the number went up.
   */
  readonly trend: Trend;
  /** What the change is measured against, for example "vs. last week". */
  readonly context?: string;
  /** Accessible wording for the direction; overrides the default "Increase"/"Decrease"/"No change". */
  readonly trendLabel?: string;
}

const ICONS: Record<Trend, typeof IconArrowUpRight> = {
  up: IconArrowUpRight,
  down: IconArrowDownRight,
  flat: IconMinus,
};
const DEFAULT_LABELS: Record<Trend, string> = {
  up: "Increase",
  down: "Decrease",
  flat: "No change",
};

/**
 * A signed value with its direction. Color is never the only signal: the arrow, the caller's sign, and a
 * screen-reader label all carry the direction, so the value survives grayscale and color-vision differences
 * (docs/UI_STANDARDS.md, data and dashboard styling).
 */
export function DeltaValue({
  children,
  trend,
  context,
  trendLabel,
  size = "sm",
  fw = 600,
  ...rest
}: DeltaValueProps) {
  const Icon = ICONS[trend];
  const role = trendRole(trend);
  return (
    <Group gap={4} wrap="nowrap" align="center">
      <Text
        component="span"
        size={size}
        fw={fw}
        c={semanticRoles[role]}
        className="how-numeric"
        {...rest}
      >
        <Icon
          size={14}
          stroke={2}
          aria-hidden
          style={{ verticalAlign: "-2px", marginInlineEnd: 2 }}
        />
        {children}
        <span className="how-visually-hidden">{` ${trendLabel ?? DEFAULT_LABELS[trend]}`}</span>
      </Text>
      {context !== undefined && (
        <Text component="span" size="xs" c="dimmed">
          {context}
        </Text>
      )}
    </Group>
  );
}
